import type {
  DashboardSaveV2Request,
  DashboardSaveV2Response,
  DscWorkbenchDashboardResponseV2,
} from "@credit-acceptance/step6-bff-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useSaveableSectionsStore } from "../../stores/saveableSectionsStore";
import { stipulationKeys } from "../stipulations/api/stipulationKeys";
import { dscWorkbenchKeys } from "./api/dscWorkbenchKeys";
import { useSaveWorkbench } from "./api/useSaveWorkbench";

/**
 * Result of a saveAll attempt — `apiCallMade` tells callers whether the
 * outcome came back from the save API (vs. a pre-flight short-circuit like no
 * sections being registered), so they can decide whether it's appropriate to
 * surface as an API-response toast.
 */
export interface SaveAllResult {
  ok: boolean;
  apiCallMade: boolean;
  /**
   * HTTP status of the save response, when the API was actually called —
   * any 2xx lands here as `ok: true`, but callers that need to distinguish
   * an exact 200 (e.g. the footer's success toast) can check this directly.
   */
  statusCode?: number;
}

/**
 * Narrows the save response's additional-products block to the GET-side shape.
 * The two diverge on `obp`: the save response returns `ObpSection` (`products`
 * plus the save-time `validations` split), while the Dashboard GET returns
 * `BackendProductListView` (`backendProducts`). Mapping it explicitly keeps the
 * cached snapshot in the shape every Dashboard consumer reads — writing the
 * save shape through would silently hand the OBP UI a payload with no
 * `backendProducts`. The `validations` split is dropped: it's a save outcome,
 * not dashboard state.
 * @param {DashboardSaveV2Response["additionalProducts"]} additionalProducts - Additional products off the v2 save response.
 * @returns {DscWorkbenchDashboardResponseV2["additionalProducts"]} The GET-shaped section, or undefined when the response carried none.
 */
function toDashboardAdditionalProducts(
  additionalProducts: DashboardSaveV2Response["additionalProducts"]
): DscWorkbenchDashboardResponseV2["additionalProducts"] {
  if (!additionalProducts) {
    return undefined;
  }
  const { obp, ...passthrough } = additionalProducts;
  return {
    ...passthrough,
    obp: obp ? { backendProducts: obp.products ?? [] } : undefined,
  };
}

/**
 * Builds the Dashboard query cache snapshot from a v2 save response. `driver`
 * / `secondSigner` on the response are `ApplicantOutcomeV2` — a superset of
 * the GET-side `ApplicantSectionV2` that additionally carries this save's
 * outcome (validations, changedFields, warnings, perStep) — so only the
 * overlapping current-state fields are picked here. The save response never
 * carries the stipulation panel (see `stipulationActionsResult`'s doc — it's
 * a workflow-execution status, not the list), so `stipulations` on the
 * snapshot is left undefined until the next GET refetch. `financial` is
 * carried over from the cached GET snapshot: the save response still returns
 * the older `SnapshotFinancialSection` shape (the GET returns
 * `FinancialSectionV2`), and financial isn't writable on save anyway.
 * @param {DashboardSaveV2Response} response - The v2 save response.
 * @param {DscWorkbenchDashboardResponseV2["financial"]} financial - The cached GET-side financial section.
 * @returns {DscWorkbenchDashboardResponseV2} The refreshed dashboard snapshot.
 */
function toDashboardSnapshot(
  response: DashboardSaveV2Response,
  financial: DscWorkbenchDashboardResponseV2["financial"]
): DscWorkbenchDashboardResponseV2 {
  return {
    metadata: response.dashboardMetadata,
    approvalId: response.approvalId,
    dealSummary: response.dealSummary,
    driver: response.driver
      ? {
          present: response.driver.present,
          applicantId: response.driver.applicantId,
          creditScore: response.driver.creditScore,
          residence: response.driver.residence,
          income: response.driver.income,
          bank: response.driver.bank,
          validations: response.driver.validations,
        }
      : undefined,
    secondSigner: response.secondSigner
      ? {
          present: response.secondSigner.present,
          applicantId: response.secondSigner.applicantId,
          creditScore: response.secondSigner.creditScore,
          residence: response.secondSigner.residence,
          income: response.secondSigner.income,
          bank: response.secondSigner.bank,
          validations: response.secondSigner.validations,
        }
      : undefined,
    vehicle: response.vehicle,
    financial,
    dealStatus: response.dealStatus,
    additionalProducts: toDashboardAdditionalProducts(response.additionalProducts),
  };
}

/**
 * Orchestrates the shared footer Save button and the Exit modal's Save & Exit:
 * runs every registered section's validation (so inline field errors still
 * surface), merges their payload contributions into one v2 Dashboard-shaped
 * save request (`DashboardSaveV2Request`), and issues a single PUT
 * /api/v2/workbench/applications/{approvalId}/Dashboard call regardless of
 * validation outcome — the BFF, not the client, is the source of truth for
 * whether a save succeeds. `ok`/`apiCallMade` reflect the actual save API
 * response, and each section's own `onSaved`/`onSaveFailed` decides how it
 * surfaces that result.
 *
 * On any successful (2xx) save, the response already carries a freshly
 * refreshed full Dashboard snapshot, so that's hydrated straight into the
 * Dashboard query cache via `setQueryData` — no follow-up GET — unless the
 * refresh failed entirely (`dashboardMetadata` absent), in which case this
 * falls back to invalidating the query so React Query refetches it. The
 * stipulations list query is still invalidated separately: `stipulationActionsResult`
 * on the response is only a workflow-execution status, not the list itself.
 * `isSaving` stays true through this whole tail, not just the save request
 * itself, so the footer's Save button can't be re-clicked mid-chain.
 * @returns {{saveAll: (approvalId: string) => Promise<SaveAllResult>, isSaving: boolean}} The orchestrator.
 */
export function useSaveAllSections() {
  const saveWorkbench = useSaveWorkbench();
  const queryClient = useQueryClient();
  const [isSequencingFollowUps, setIsSequencingFollowUps] = useState(false);

  const saveAll = useCallback(
    async (approvalId: string): Promise<SaveAllResult> => {
      const entries = [...useSaveableSectionsStore.getState().sections.entries()];
      // Fail closed if nothing has registered yet (e.g. Save clicked before a
      // section mounts) rather than silently succeeding an empty-payload save.
      if (entries.length === 0) {
        return { ok: false, apiCallMade: false };
      }

      // Run validation for its side effect (surfacing inline errors); the
      // result no longer gates the save — a section stays invalid client-side
      // but still gets saved as-is, so a cleared/blank field always persists.
      await Promise.all(entries.map(([, section]) => section.validate()));

      const payload: DashboardSaveV2Request = Object.assign({}, ...entries.map(([, section]) => section.getPayload()));

      try {
        const { response, status } = await saveWorkbench.mutateAsync({ approvalId, payload });
        for (const [, section] of entries) {
          section.onSaved({
            metadata: response.metadata,
            driver: response.driver,
            secondSigner: response.secondSigner,
          });
        }
        // Sequenced follow-up: the stipulations list refresh, awaited so the
        // Exit modal's move-account call fires only after it has settled.
        setIsSequencingFollowUps(true);
        try {
          if (response.dashboardMetadata) {
            const dashboardKey = dscWorkbenchKeys.detail({ approvalId });
            const cachedFinancial = queryClient.getQueryData<DscWorkbenchDashboardResponseV2>(dashboardKey)?.financial;
            queryClient.setQueryData(dashboardKey, toDashboardSnapshot(response, cachedFinancial));
          } else {
            // The post-save dashboard refresh failed entirely — fall back to
            // invalidating so React Query issues a fresh GET.
            await queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
          }

          await queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() });
        } finally {
          setIsSequencingFollowUps(false);
        }
        return { ok: true, apiCallMade: true, statusCode: status };
      } catch (error) {
        for (const [, section] of entries) {
          section.onSaveFailed(error);
        }
        return { ok: false, apiCallMade: true };
      }
    },
    [queryClient, saveWorkbench]
  );

  return { saveAll, isSaving: saveWorkbench.isPending || isSequencingFollowUps };
}
